// Task 32 — per-engine catalog queries backing `hasSpatialIndex` in `spatialIndexAdvisor.ts`.
// Split out purely to keep the advisor file under the repo's line-count guideline; not a
// standalone public contract (only `spatialIndexAdvisor.ts` imports `probeSpatialIndex`).
// One query per engine, verified against each catalog's real shape at implementation time:
// pgsql `pg_indexes` (indexdef text — regex the `USING gist/spgist (...)` column list) · mysql
// `information_schema.statistics` (`INDEX_TYPE = 'SPATIAL'`) · mssql `sys.spatial_indexes` joined
// down to the column · oracle `ALL_INDEXES` (`ITYP_NAME LIKE 'SPATIAL_INDEX%'`) joined to
// `ALL_IND_COLUMNS` · sqlite the SpatiaLite `idx_<table>_<column>` companion table's existence.

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { QueryColumn } from '../../../../types/queryExecution';
import { createExecutionId } from '../../../../types/queryExecution';
import { executeQueryV2 } from '../../../database-objects/services/queryExecutor';

function escapeLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

function getCell(columns: QueryColumn[], row: Record<string, unknown>, columnName: string): unknown {
  const col = columns.find((c) => (c.name ?? '').toLowerCase() === columnName.toLowerCase());
  const key = col?.id ?? col?.name ?? columnName;
  return row[key];
}

async function runCatalogProbe(args: {
  connectionId: string;
  database: string;
  sql: string;
}): Promise<{ columns: QueryColumn[]; rows: Record<string, unknown>[] } | null> {
  try {
    const response = await executeQueryV2({
      connectionId: args.connectionId,
      database: args.database,
      sql: args.sql,
      executionId: createExecutionId('spatial-index-probe'),
    });
    if (!response.success) return null;
    const rs = response.resultSets?.[0];
    if (!rs) return null;
    return { columns: rs.columns, rows: rs.rows };
  } catch {
    return null;
  }
}

/** Dispatches the per-engine catalog probe; `null` on any error/unsupported engine (see `hasSpatialIndex`). */
export async function probeSpatialIndex(
  engine: DatabaseEngineId,
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string
): Promise<boolean | null> {
  switch (engine) {
    case 'pgsql': {
      const probe = await runCatalogProbe({
        connectionId,
        database,
        sql: `SELECT indexdef FROM pg_indexes WHERE schemaname = '${escapeLiteral(schema)}' AND tablename = '${escapeLiteral(table)}'`,
      });
      if (!probe) return null;
      const col = column.toLowerCase();
      return probe.rows.some((row) => {
        const indexdef = String(getCell(probe.columns, row, 'indexdef') ?? '').toLowerCase();
        if (!/using\s+(gist|spgist)/.test(indexdef)) return false;
        const colsMatch = indexdef.match(/\(([^)]*)\)\s*$/);
        if (!colsMatch) return false;
        return colsMatch[1].split(',').map((c) => c.trim().replace(/"/g, '')).includes(col);
      });
    }
    case 'mysql': {
      const probe = await runCatalogProbe({
        connectionId,
        database,
        sql: `SELECT INDEX_TYPE FROM information_schema.statistics WHERE table_schema = '${escapeLiteral(database)}' AND table_name = '${escapeLiteral(table)}' AND column_name = '${escapeLiteral(column)}'`,
      });
      if (!probe) return null;
      return probe.rows.some((row) => String(getCell(probe.columns, row, 'INDEX_TYPE') ?? '').toUpperCase() === 'SPATIAL');
    }
    case 'mssql': {
      const probe = await runCatalogProbe({
        connectionId,
        database,
        sql: `SELECT si.name FROM sys.spatial_indexes si
JOIN sys.index_columns ic ON ic.object_id = si.object_id AND ic.index_id = si.index_id
JOIN sys.columns c ON c.object_id = ic.object_id AND c.column_id = ic.column_id
JOIN sys.tables t ON t.object_id = si.object_id
JOIN sys.schemas s ON s.schema_id = t.schema_id
WHERE s.name = '${escapeLiteral(schema)}' AND t.name = '${escapeLiteral(table)}' AND c.name = '${escapeLiteral(column)}'`,
      });
      if (!probe) return null;
      return probe.rows.length > 0;
    }
    case 'oracle': {
      const probe = await runCatalogProbe({
        connectionId,
        database,
        sql: `SELECT ai.ityp_name FROM all_indexes ai
JOIN all_ind_columns aic ON aic.index_name = ai.index_name AND aic.index_owner = ai.owner
WHERE ai.table_name = UPPER('${escapeLiteral(table)}') AND aic.column_name = UPPER('${escapeLiteral(column)}')
AND ai.ityp_name LIKE 'SPATIAL_INDEX%'`,
      });
      if (!probe) return null;
      return probe.rows.length > 0;
    }
    case 'sqlite': {
      const probe = await runCatalogProbe({
        connectionId,
        database,
        sql: `SELECT name FROM sqlite_master WHERE type = 'table' AND name = 'idx_${escapeLiteral(table)}_${escapeLiteral(column)}'`,
      });
      if (!probe) return null;
      return probe.rows.length > 0;
    }
    default:
      return null;
  }
}
